import { label as fmtLabel, setNumberPrompt, ToastCtx, t, useToast } from "@keel/core";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tip } from "./Help";

export { useToast };

// ── icons (1.6 stroke on a 24 grid) ─────────────────────────────────────
const PATHS: Record<string, string> = {
  home: "M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z",
  cart: "M3 4h2l2.4 11.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 8H6.2M9 20.5h.01M17 20.5h.01",
  invoice: "M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5",
  truck:
    "M3 6h11v10H3zM14 10h4l3 3v3h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  receipt: "M5 3h14v18l-3.5-2-3.5 2-3.5-2L5 21zM9 8h6M9 12h6",
  box: "M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8",
  boxes: "M4 13h7v7H4zM13 13h7v7h-7zM8.5 4h7v7h-7z",
  moves: "M7 4v16M7 4 3 8M7 4l4 4M17 20V4M17 20l-4-4M17 20l4-4",
  users:
    "M16 20v-1.5A3.5 3.5 0 0 0 12.5 15h-5A3.5 3.5 0 0 0 4 18.5V20M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 20v-1.5a3.5 3.5 0 0 0-2.5-3.3M15.5 4.2a3.5 3.5 0 0 1 0 6.6",
  factory: "M3 21V10l6 3.5V10l6 3.5V5h6v16zM7 17h2M12 17h2M17 17h2",
  ledger: "M4 4h12a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4zM8 9h8M8 13h8M8 17h5",
  book: "M4 5a2 2 0 0 1 2-2h14v15H6a2 2 0 0 0-2 2zM4 20a2 2 0 0 0 2 2h14v-4",
  chart: "M4 20V10M10 20V4M16 20v-7M22 20H2",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4",
  plus: "M12 5v14M5 12h14",
  x: "M6 6l12 12M18 6 6 18",
  pin: "M9 4h6M10 4v5.5L7 13h10l-3-3.5V4M12 13v7",
  check: "M5 12.5 10 17 19 7",
  alert: "M12 8v5M12 16.5v.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z",
  sun: "M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  moon: "M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z",
  chevron: "M9 6l6 6-6 6",
  down: "M6 9l6 6 6-6",
  updown: "M8 9l4-4 4 4M8 15l4 4 4-4",
  arrowUp: "M12 19V5M6 11l6-6 6 6",
  arrowDown: "M12 5v14M6 13l6 6 6-6",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  trash: "M4 7h16M10 11v6M14 11v6M5 7l1 13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1l1-13M9 7V4h6v3",
  edit: "M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4",
  print: "M7 9V3h10v6M7 17H4v-7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v7h-3M7 14h10v7H7z",
  filter: "M3 5h18l-7 8v6l-4 2v-8z",
  sliders: "M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M14 4v4M8 10v4M16 16v4",
  plug: "M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0zM12 17v4",
  webhook:
    "M8.5 16.5 12 10M12 10a3 3 0 1 0-3-3M15.5 16.5H9M15.5 16.5a3 3 0 1 0 3-3M8.5 16.5a3 3 0 1 0 0 0zM18.5 13.5 15 7.5",
  key: "M15 9a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM12 12v9M12 16h3M12 19h2",
  shield: "M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z",
  history: "M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z",
  warehouse: "M3 21V9l9-5 9 5v12M7 21v-7h10v7M7 17h10",
  code: "M9 8l-4 4 4 4M15 8l4 4-4 4",
  copy: "M9 9h11v11H9zM5 15H4V4h11v1",
  refresh: "M20 11a8 8 0 0 0-14.8-4M4 5v4h4M4 13a8 8 0 0 0 14.8 4M20 19v-4h-4",
  pause: "M8 5v14M16 5v14",
  play: "M7 4v16l13-8z",
  bolt: "M13 3 4 14h7l-1 7 9-11h-7z",
  logout: "M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 16l-4-4 4-4M6 12h10",
  cash: "M3 6h18v12H3zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 9v.01M18 15v.01",
  send: "M4 12 20 4l-4 16-4-7zM12 13l8-9",
  building: "M4 21V4h11v17M15 9h5v12M8 8h3M8 12h3M8 16h3",
  cog: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1",
  calendar: "M4 6h16v15H4zM4 10h16M8 3v4M16 3v4M8 14h2M14 14h2M8 17h2",
  tree: "M9 4h6v4H9zM3 16h6v4H3zM15 16h6v4h-6zM12 8v4M6 16v-4h12v4",
  gauge: "M4 18a8 8 0 1 1 16 0M12 18l4-6M7 14h.01M17 14h.01M12 9v.01",
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  file: "M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7zM14 3v4h4",
  image: "M4 5h16v14H4zM4 15l4.5-4.5 4 4 3-3L20 16M9.5 9.5h.01",
  paperclip:
    "M20 10.5 11 19.5a4.5 4.5 0 0 1-6.4-6.4l9-9a3 3 0 0 1 4.3 4.3l-9 9a1.5 1.5 0 0 1-2.2-2.1l8.3-8.3",
  comment: "M4 5h16v11H9l-5 4z",
  wallet: "M4 7a2 2 0 0 1 2-2h12v4M4 7v11a2 2 0 0 0 2 2h14V9H6a2 2 0 0 1-2-2zM16 14.5h.01",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0",
  lock: "M6 11h12v10H6zM8.5 11V7.5a3.5 3.5 0 0 1 7 0V11",
  help: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.2-2.5 3.9M12 17h.01",
  sparkle: "M12 3v4M12 17v4M3 12h4M17 12h4M12 8l1.5 2.5L16 12l-2.5 1.5L12 16l-1.5-2.5L8 12l2.5-1.5z",
  expand: "M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7",
  shrink: "M20 10h-6V4M4 14h6v6M14 10l7-7M10 14l-7 7",
  timer: "M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM12 9v4l2.5 2M9 2h6",
};
export const Icon = ({
  name,
  size = 16,
  className = "",
}: {
  name: string;
  size?: number;
  className?: string;
}) => (
  <svg
    className={`i ${className}`}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={PATHS[name] ?? ""} />
  </svg>
);

// ── status pill ─────────────────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  posted: "Open",
  partially_paid: "Part paid",
  partially_delivered: "Part delivered",
  partially_received: "Part received",
  dead: "Failed",
  failed: "Retrying",
  ready: "Ready for signing",
};
export const Pill = ({ status, children }: { status: string; children?: ReactNode }) => (
  <span className={`pill pill-${status}`}>
    <i />
    {children ?? STATUS_LABEL[status] ?? fmtLabel(status)}
  </span>
);

export const Empty = ({
  title,
  text,
  action,
  icon = "box",
}: {
  title: string;
  text?: string;
  action?: ReactNode;
  icon?: string;
}) => (
  <div className="empty">
    <div className="empty-mark">
      <Icon name={icon} size={20} />
    </div>
    <h3>{title}</h3>
    {text && <p>{text}</p>}
    {action}
  </div>
);

/** `loans.applications.read` → "Loans · Applications · read", the way the role editor lists it. */
export const permissionPhrase = (key: string) =>
  key === "*"
    ? t("Everything")
    : key
        .split(".")
        .map((part, i, all) =>
          part === "*" ? t("all") : i === all.length - 1 && i > 0 ? t(part) : fmtLabel(part),
        )
        .join(" · ");

/**
 * A page the user's role does not cover (ERP-162): says which permission is missing and who can
 * give it, instead of a loader that never finishes or a page of zeros.
 */
export const NoAccess = ({ permission, onRetry }: { permission?: string | null; onRetry?: () => void }) => (
  <div className="page">
    <Empty
      icon="lock"
      title={t("No access")}
      text={
        permission
          ? t(
              "This page needs the permission {perm}, which your role doesn't have. Ask an administrator to add it.",
              {
                perm: `${permissionPhrase(permission)} (${permission})`,
              },
            )
          : t("Your role doesn't cover this page. Ask an administrator to add it.")
      }
      action={
        <div className="row" style={{ gap: 8, justifyContent: "center" }}>
          {onRetry && (
            <button type="button" className="btn" onClick={onRetry}>
              {t("Check again")}
            </button>
          )}
          <Link className="btn btn-ghost" to="/">
            {t("Go to Overview")}
          </Link>
        </div>
      }
    />
  </div>
);

export const Skeleton = ({ rows = 6 }: { rows?: number }) => (
  <div className="page">
    <div className="skeleton">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ width: `${90 - (i % 3) * 18}%` }} />
      ))}
    </div>
  </div>
);

export const Field = ({
  label,
  hint,
  tip,
  children,
  span,
  required,
}: {
  label: string;
  hint?: ReactNode;
  /** What the field means and what it changes, behind a "?" beside the label (ERP-67). */
  tip?: ReactNode;
  children: ReactNode;
  span?: boolean;
  /** Marks the label, so a form that refuses to save has already said what it needs. */
  required?: boolean;
}) => {
  const text = (
    <span>
      {label}
      {required && (
        <span className="req" title="Required">
          {" "}
          *
        </span>
      )}
    </span>
  );
  if (!tip)
    return (
      // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children
      <label className={`field ${span ? "span-2" : ""}`}>
        {text}
        {children}
        {hint && <small>{hint}</small>}
      </label>
    );
  // The tip sits beside the label, not inside it: a <label> belongs to its first control, and a
  // button inside would take the field's label from the input.
  return (
    <div className={`field field-tipped ${span ? "span-2" : ""}`}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children */}
      <label className="field-label">
        {text}
        {children}
        {hint && <small>{hint}</small>}
      </label>
      <Tip>{tip}</Tip>
    </div>
  );
};

/**
 * A toggle with its label beside it.
 *
 * The label used to be the accessible name only, so a switch on its own rendered as a bare toggle
 * with nothing to say what it did — two of them side by side were a guessing game.
 */
export const Switch = ({
  checked,
  onChange,
  label,
  hint,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  /** One line under the label saying what turning it on actually does. */
  hint?: string;
  /** Read out but not shown, for a switch whose meaning the row or column already says. */
  ariaLabel?: string;
}) => {
  // The input lives inside the <label>, which is what associates them; the lint rule only
  // recognises the pattern when the control is a direct child, so it sits here rather than in a
  // nested helper.
  return (
    <label className="switch-row">
      <span className="switch">
        <input
          type="checkbox"
          checked={checked}
          aria-label={ariaLabel ?? label}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span />
      </span>
      {(label || hint) && (
        <span className="switch-text">
          {label}
          {hint && <small>{hint}</small>}
        </span>
      )}
    </label>
  );
};

export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; count?: number }[];
}) {
  return (
    <div className="tabs" role="tablist">
      {options.map((o) => (
        <button
          type="button"
          key={o.value}
          className={`tab ${o.value === value ? "active" : ""}`}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
        >
          <span>{o.label}</span>
          {o.count !== undefined && <span className="tab-n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="search-input">
      <Icon name="search" size={14} />
      <input
        data-search
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
    </label>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      <Icon name={done ? "check" : "copy"} size={13} />
      {done ? "Copied" : label}
    </button>
  );
}

// ── modal ───────────────────────────────────────────────────────────────
export function Modal({
  title,
  children,
  onClose,
  onSubmit,
  submit,
  danger,
  width = 480,
  busy,
  error,
  footer,
  dirty,
  discard,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  /** Something has been typed: closing by Esc, the backdrop, ✕ or Cancel asks first (ERP-52). */
  dirty?: boolean;
  /** The question asked, e.g. "Discard this application?". */
  discard?: string;
  onSubmit?: () => void | Promise<void>;
  submit?: string;
  danger?: boolean;
  width?: number;
  busy?: boolean;
  error?: string | null;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [asking, setAsking] = useState(false);
  const close = () => (dirty ? setAsking(true) : onClose());
  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    const first = d.querySelector<HTMLInputElement>(
      "input:not([type=hidden]):not([type=checkbox]), select, textarea",
    );
    first?.focus();
    first?.select?.();
    return () => d.close();
  }, []);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click closes; Escape closes natively
    <dialog
      ref={ref}
      className="modal"
      style={{ width: `min(${width}px, calc(100vw - 32px))` }}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        if (e.target === ref.current) close();
      }}
    >
      <form
        method="dialog"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <header>
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={close} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        <footer>
          <span className="modal-error" role="alert">
            {error}
          </span>
          {/* Keyed, so React can't reuse "Keep editing" as the submit button mid-click: the browser would
              then submit the form that same click. */}
          {asking ? (
            <>
              <span key="ask" className="strong">
                {discard ?? t("Discard what you've entered?")}
              </span>
              {/* biome-ignore lint/a11y/noAutofocus: the question needs the focus, or Esc would close it */}
              <button key="keep" type="button" className="btn" autoFocus onClick={() => setAsking(false)}>
                {t("Keep editing")}
              </button>
              <button key="discard" type="button" className="btn btn-danger" onClick={onClose}>
                {t("Discard")}
              </button>
            </>
          ) : (
            (footer ?? (
              <>
                <button type="button" className="btn" onClick={close}>
                  {t(onSubmit ? "Cancel" : "Close")}
                </button>
                {onSubmit && (
                  <button
                    type="submit"
                    className={`btn ${danger ? "btn-danger" : "btn-primary"}`}
                    disabled={busy}
                  >
                    {submit ?? t("Save")}
                  </button>
                )}
              </>
            ))
          )}
        </footer>
      </form>
    </dialog>
  );
}

// ── toasts ──────────────────────────────────────────────────────────────
type Toast = { id: number; msg: string; kind: "ok" | "error" };
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((msg: string, kind: "ok" | "error" = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 5000 : 2800);
  }, []);
  // A modal is a <dialog> in the browser's top layer, above any z-index — so a toast raised while
  // one was open (a refusal from the server, typically) was drawn under its backdrop, unreadable.
  // As a manual popover the stack lives in the top layer too, and showing it again puts it on top.
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = host.current;
    if (!el?.showPopover || toasts.length === 0) return;
    try {
      if (el.matches(":popover-open")) el.hidePopover();
      el.showPopover();
    } catch {
      /* not supported: the fixed position below still shows it outside dialogs */
    }
  }, [toasts]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div id="toasts" ref={host} popover="manual" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind} in`}>
            <Icon name={t.kind === "error" ? "alert" : "check"} />
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function Stat({
  label,
  value,
  foot,
  href,
  tone,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  href?: string;
  tone?: string;
}) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className={`stat-value ${tone ? `tone-${tone}` : ""}`}>{value}</span>
      {foot && <span className="stat-foot">{foot}</span>}
    </>
  );
  return href ? (
    <Link className="stat" to={href}>
      {body}
    </Link>
  ) : (
    <div className="stat">{body}</div>
  );
}

export const ConnectorLogo = ({ connector, lg }: { connector: string; lg?: boolean }) => {
  const txt: Record<string, string> = {
    shopify: "S",
    magento: "M",
    woocommerce: "W",
    pim: "PIM",
    wms: "WMS",
    marketplace: "MP",
    d365: "D",
    custom_api: "{ }",
    dokobit: "DK",
    kib: "KIB",
  };
  return (
    <span
      className={`logo logo-${connector} ${lg ? "lg" : ""}`}
      style={{ fontSize: (txt[connector]?.length ?? 1) > 2 ? 10 : undefined }}
    >
      {txt[connector] ?? "?"}
    </span>
  );
};

/**
 * The app's own "are you sure?" and "why?" dialogs, instead of the browser's confirm() and
 * prompt() (ERP-167): styled like the rest, readable, and testable. Mount `AskHost` once; then
 * `await confirmDialog(…)` is true on OK, `await askText(…)` is the text typed, or null on cancel.
 */
type Ask =
  | { kind: "confirm"; message: string; ok: string; danger: boolean; resolve: (v: boolean) => void }
  | {
      kind: "text";
      message: string;
      label: string;
      ok: string;
      required: boolean;
      initial: string;
      resolve: (v: string | null) => void;
    };
let showAsk: ((a: Ask) => void) | null = null;

export function confirmDialog(
  message: string,
  opts: { ok?: string; danger?: boolean } = {},
): Promise<boolean> {
  if (!showAsk) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) =>
    showAsk?.({ kind: "confirm", message, ok: opts.ok ?? "OK", danger: opts.danger ?? false, resolve }),
  );
}

export function askText(
  message: string,
  opts: { label?: string; ok?: string; required?: boolean; initial?: string } = {},
): Promise<string | null> {
  if (!showAsk) return Promise.resolve(window.prompt(message, opts.initial ?? ""));
  return new Promise((resolve) =>
    showAsk?.({
      kind: "text",
      message,
      label: opts.label ?? "Reason",
      ok: opts.ok ?? "OK",
      required: opts.required ?? true,
      initial: opts.initial ?? "",
      resolve,
    }),
  );
}

export function AskHost() {
  const [ask, setAsk] = useState<Ask | null>(null);
  const [value, setValue] = useState("");
  useEffect(() => {
    showAsk = (a) => {
      setValue(a.kind === "text" ? a.initial : "");
      setAsk(a);
    };
    return () => {
      showAsk = null;
    };
  }, []);
  if (!ask) return null;
  const close = () => {
    if (ask.kind === "confirm") ask.resolve(false);
    else ask.resolve(null);
    setAsk(null);
  };
  const ok = () => {
    if (ask.kind === "confirm") ask.resolve(true);
    else ask.resolve(value.trim());
    setAsk(null);
  };
  return (
    <Modal
      title={ask.kind === "confirm" ? "Please confirm" : ask.message}
      onClose={close}
      submit={ask.ok}
      danger={ask.kind === "confirm" && ask.danger}
      busy={ask.kind === "text" && ask.required && !value.trim()}
      onSubmit={ok}
    >
      <div className="stack" data-testid="ask-dialog">
        {ask.kind === "confirm" ? (
          <p style={{ margin: 0, whiteSpace: "pre-line" }}>{ask.message}</p>
        ) : (
          <Field label={ask.label} required={ask.required}>
            <textarea rows={3} value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/**
 * The dialog that asks for a document number typed by hand (ERP-115): mounted once, it answers
 * the API client whenever a series numbered manually needs one.
 */
export function NumberPromptHost() {
  const [ask, setAsk] = useState<{ message: string; resolve: (v: string | null) => void } | null>(null);
  const [value, setValue] = useState("");
  useEffect(() => {
    setNumberPrompt(
      (message) =>
        new Promise<string | null>((resolve) => {
          setValue("");
          setAsk({ message, resolve });
        }),
    );
    return () => setNumberPrompt(null);
  }, []);
  if (!ask) return null;
  const done = (v: string | null) => {
    ask.resolve(v);
    setAsk(null);
  };
  return (
    <Modal
      title="Document number"
      onClose={() => done(null)}
      submit="Use this number"
      onSubmit={() => done(value)}
    >
      <div className="stack" data-testid="number-prompt">
        <p className="sm" style={{ margin: 0 }}>
          {ask.message}
        </p>
        <Field label="Number">
          <input className="mono" value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} />
        </Field>
      </div>
    </Modal>
  );
}
